/**
 * Turning notes into grace notes, and grace notes back into notes.
 *
 * This is the one mark that is arithmetic and not decoration. Every other mark leaves the
 * bar exactly as it found it; a grace note gives up the time it was written with -- it takes
 * its sounding time from the note it leans on instead -- and a bar that loses a crotchet is
 * a bar that no longer adds up. So the time is accounted for on both sides: a note becoming
 * a grace leaves silence where it stood, and a grace becoming a note again takes its time
 * back out of what follows it, which is what writing a note over that moment would do.
 *
 * The voice is therefore rewritten rather than the event replaced, and the selected notes of
 * one voice are worked through from the last backwards, so that the ones still to come keep
 * the indices they were found at.
 */

import { ticksOf } from "../document/duration";
import { capacityOf, eventOnsets } from "../document/query";
import type { MusicEvent, NoteEvent, Score, Voice } from "../document/types";
import type { Command } from "../commands/types";
import { selectedNotes } from "./selection";
import type { NoteLocation, Selection } from "./selection";
import { overwrite, silence } from "./entry-voice";

/** One note of a voice made a grace, or made an ordinary note again. */
function regraced(
  voice: Voice,
  index: number,
  kind: NoteEvent["grace"],
  capacity: number,
): readonly MusicEvent[] | string {
  const event = voice.events[index];
  if (event === undefined || event.kind !== "note") return "there is no note there";
  if (event.grace === kind) return voice.events;

  const before = voice.events.slice(0, index);
  const after = voice.events.slice(index + 1);

  if (kind !== null) {
    // Already a grace: only the kind of grace changes, and no time moves.
    if (event.grace !== null) return [...before, { ...event, grace: kind }, ...after];
    const freed = silence(ticksOf(event.duration));
    if (freed === null) return "the time that note gave up cannot be written as plain rests";
    return [...before, { ...event, grace: kind }, ...freed, ...after];
  }

  // A grace taking its time back is a note written at the moment it was leaning on, which is
  // where the notes after it stand: the ordinary overwrite says what happens to them.
  const onset = eventOnsets(voice)[index] ?? 0;
  const without = { ...voice, events: [...before, ...after] };
  const written = overwrite(without, onset, { ...event, grace: null }, capacity);
  return typeof written === "string" ? written : written.events;
}

/** The selected notes gathered by the voice they stand in, last first within each voice. */
function byVoice(found: readonly NoteLocation[]): readonly NoteLocation[][] {
  const groups = new Map<string, NoteLocation[]>();
  for (const at of found) {
    const key = [at.partId, at.measureId, at.voiceIndex].join(" ");
    groups.set(key, [...(groups.get(key) ?? []), at]);
  }
  return [...groups.values()].map((group) =>
    [...group].sort((one, other) => other.eventIndex - one.eventIndex),
  );
}

/**
 * Turn the selection into grace notes of the given kind, or back into notes of their own.
 *
 * A grace note keeps its written length -- that is what decides how it is drawn -- and
 * sounds by stealing from the note after it, so what changes in the bar is who accounts for
 * the time and not how much of it there is.
 */
export function grace(
  score: Score,
  selection: Selection,
  kind: NoteEvent["grace"],
): Command | string {
  const found = selectedNotes(score, selection);
  if (found.length === 0) return "no note is selected";

  const commands: Command[] = [];
  for (const group of byVoice(found)) {
    const first = group[0];
    if (first === undefined) continue;
    const part = score.parts.find((candidate) => candidate.id === first.partId);
    const measure = part?.measures[first.measureId];
    let voice = measure?.voices[first.voiceIndex];
    if (part === undefined || measure === undefined || voice === undefined) continue;

    const capacity = capacityOf(score, first.measureIndex);
    for (const at of group) {
      const events = regraced(voice, at.eventIndex, kind, capacity);
      if (typeof events === "string") return events;
      voice = { ...voice, events };
    }

    const rewritten = voice;
    commands.push({
      kind: "setPartMeasure",
      partId: part.id,
      measureId: first.measureId,
      measure: {
        ...measure,
        voices: measure.voices.map((held, index) =>
          index === first.voiceIndex ? rewritten : held,
        ),
      },
    });
  }

  if (commands.length === 0) return "nothing selected can be a grace note";
  return { kind: "batch", label: kind === null ? "no grace" : kind, commands };
}
